const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

class RentsNoticeToQuitService {
  // Get all notice to quit records (DataTable compatible)
  async getAll(dataTableParams = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rents/notice-to-quit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(dataTableParams),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(
        "RentsNoticeToQuit Service: API error, using mock data:",
        error.message
      );
      return this.getMockData();
    }
  }

  // Get notice to quit by rent ID
  async getByRentId(rentId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/rents/notice-to-quit/rent/${rentId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(
        "RentsNoticeToQuit Service: API error, using mock data:",
        error.message
      );
      return this.getMockDataByRentId(rentId);
    }
  }

  // Create new notice to quit
  async create(noticeToQuitData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/rents/notice-to-quit/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(noticeToQuitData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log("RentsNoticeToQuit Service: API error:", error.message);
      return {
        success: false,
        message: "Failed to create notice to quit",
        error: error.message,
      };
    }
  }

  // Update notice to quit
  async update(id, updateData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/rents/notice-to-quit/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log("RentsNoticeToQuit Service: API error:", error.message);
      return {
        success: false,
        message: "Failed to update notice to quit",
        error: error.message,
      };
    }
  }

  // Delete notice to quit file (matches Laravel: RentNoticeToQuitController@delete)
  // Laravel route: DELETE /{rent}/notice-to-quit/delete
  // Only deletes the file, sets file = null, keeps NTQ record
  async deleteFile(rentId) {
    try {
      console.log(`[NTQ Delete] Deleting file for rentId: ${rentId}`);
      
      const response = await fetch(
        `${API_BASE_URL}/api/rents/${rentId}/notice-to-quit/delete`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`[NTQ Delete] File deleted successfully`);
      return data;
    } catch (error) {
      console.error("RentsNoticeToQuit Service: Delete file error:", error);
      throw error; // Re-throw to let caller handle error display
    }
  }

  // Legacy method - kept for backward compatibility
  // Use deleteFile(rentId) instead
  async delete(id) {
    console.warn("[NTQ Delete] Using deprecated delete(id) method. Use deleteFile(rentId) instead.");
    // This method signature doesn't match Laravel - should use rentId
    // For now, we'll try to use it as rentId if it's a number
    return this.deleteFile(id);
  }

  // Get statistics
  async getStatistics() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/rents/notice-to-quit/statistics`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(
        "RentsNoticeToQuit Service: API error, using mock data:",
        error.message
      );
      return this.getMockStatistics();
    }
  }

  // Upload file for notice to quit
  async uploadFile(rentId, fileData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/rents/notice-to-quit/${rentId}/upload`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(fileData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log("RentsNoticeToQuit Service: API error:", error.message);
      return {
        success: false,
        message: "Failed to upload file",
        error: error.message,
      };
    }
  }

  // Get presigned URL for direct S3 download (preferred method)
  async getPresignedUrl(rentId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/rents/${rentId}/notice-to-quit/presigned-url`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("RentsNoticeToQuit Service: Presigned URL error:", error);
      throw error;
    }
  }

  // Download file for notice to quit using presigned URL (direct S3 download)
  async downloadFile(rentId) {
    try {
      console.log(`[NTQ Download] Starting download for rentId: ${rentId}`);
      
      // Get presigned URL from backend
      const presignedData = await this.getPresignedUrl(rentId);
      console.log(`[NTQ Download] Got presigned URL data`);

      if (!presignedData || !presignedData.success || !presignedData.presignedUrl) {
        throw new Error(presignedData?.message || "Failed to get presigned URL");
      }

      const { presignedUrl, filename } = presignedData;
      const downloadFilename = filename || `notice-to-quit-${rentId}.pdf`;
      
      console.log(`[NTQ Download] Presigned URL received, fetching file...`);

      // Fetch file from S3 presigned URL
      const fileResponse = await fetch(presignedUrl, {
        method: "GET",
        mode: 'cors',
      });

      if (!fileResponse.ok) {
        throw new Error(`Failed to download file from S3: ${fileResponse.status}`);
      }

      // Get file as blob
      const blob = await fileResponse.blob();
      console.log(`[NTQ Download] Blob received: ${blob.size} bytes`);
      
      if (blob.size === 0) {
        throw new Error("Downloaded file is empty (0 bytes)");
      }

      // Create blob URL (same-origin, so download attribute will work)
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFilename;
      link.style.display = 'none';
      
      // Add to DOM, trigger click, then remove
      document.body.appendChild(link);
      console.log(`[NTQ Download] Triggering download for: ${downloadFilename}`);
      link.click();
      
      // Clean up after download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        console.log(`[NTQ Download] Cleanup complete`);
      }, 200);

      return { success: true, message: "File downloaded successfully" };
    } catch (error) {
      console.error("[NTQ Download] Download error:", error);
      throw error; // Re-throw to let caller handle error display
    }
  }

  // Mock data methods
  getMockData() {
    return {
      draw: 1,
      recordsTotal: 5,
      recordsFiltered: 5,
      data: [
        {
          id: 1,
          creator: "John Manager",
          guardian: "Sarah Johnson",
          guardianId: 101,
          room: "Room 101",
          propertyName: "12 Savage Road",
          propertyId: 1,
          date_notice: "15/01/2025 14:30",
          actions: `
            <button class="btn btn-sm btn-info" onclick="editNoticeToQuit(1)">Edit</button>
            <button class="btn btn-sm btn-danger" onclick="deleteNoticeToQuit(1)">Delete</button>
            <button class="btn btn-sm btn-success" onclick="downloadNoticeToQuit(1)">Download</button>
          `,
        },
        {
          id: 2,
          creator: "Jane Supervisor",
          guardian: "Michael Brown",
          guardianId: 102,
          room: "Room 102",
          propertyName: "35 University Road",
          propertyId: 2,
          date_notice: "14/01/2025 10:15",
          actions: `
            <button class="btn btn-sm btn-info" onclick="editNoticeToQuit(2)">Edit</button>
            <button class="btn btn-sm btn-danger" onclick="deleteNoticeToQuit(2)">Delete</button>
            <button class="btn btn-sm btn-success" onclick="downloadNoticeToQuit(2)">Download</button>
          `,
        },
        {
          id: 3,
          creator: "Admin User",
          guardian: "Emma Wilson",
          guardianId: 103,
          room: "Room 201",
          propertyName: "45 High Street",
          propertyId: 3,
          date_notice: "13/01/2025 16:45",
          actions: `
            <button class="btn btn-sm btn-info" onclick="editNoticeToQuit(3)">Edit</button>
            <button class="btn btn-sm btn-danger" onclick="deleteNoticeToQuit(3)">Delete</button>
            <button class="btn btn-sm btn-success" onclick="downloadNoticeToQuit(3)">Download</button>
          `,
        },
        {
          id: 4,
          creator: "John Manager",
          guardian: "David Smith",
          guardianId: 104,
          room: "Room 301",
          propertyName: "78 Park Lane",
          propertyId: 4,
          date_notice: "12/01/2025 09:30",
          actions: `
            <button class="btn btn-sm btn-info" onclick="editNoticeToQuit(4)">Edit</button>
            <button class="btn btn-sm btn-danger" onclick="deleteNoticeToQuit(4)">Delete</button>
            <button class="btn btn-sm btn-success" onclick="downloadNoticeToQuit(4)">Download</button>
          `,
        },
        {
          id: 5,
          creator: "Jane Supervisor",
          guardian: "Lisa Davis",
          guardianId: 105,
          room: "Room 401",
          propertyName: "123 Main Street",
          propertyId: 5,
          date_notice: "11/01/2025 13:20",
          actions: `
            <button class="btn btn-sm btn-info" onclick="editNoticeToQuit(5)">Edit</button>
            <button class="btn btn-sm btn-danger" onclick="deleteNoticeToQuit(5)">Delete</button>
            <button class="btn btn-sm btn-success" onclick="downloadNoticeToQuit(5)">Download</button>
          `,
        },
      ],
      success: true,
      message: "Found 5 notice to quit records",
    };
  }

  getMockDataByRentId(rentId) {
    return {
      success: true,
      data: {
        id: 1,
        rent_id: rentId,
        creator_id: 1,
        date_notice: "2025-01-15T14:30:00.000Z",
        file: "notice_to_quit_document.pdf",
        creator_first_name: "John",
        creator_last_name: "Manager",
        guardian_first_name: "Sarah",
        guardian_last_name: "Johnson",
        guardian_email: "sarah.johnson@example.com",
        guardian_mobile: "07123456789",
        room_name: "Room 101",
        property_name: "12 Savage Road",
        property_address: "12 Savage Road, Plymouth",
      },
      message: "Notice to quit found",
    };
  }

  getMockStatistics() {
    return {
      success: true,
      data: {
        total_notices: 25,
        today_notices: 3,
        week_notices: 8,
        month_notices: 25,
      },
      message: "Statistics retrieved successfully",
    };
  }
}

export default new RentsNoticeToQuitService();


